import { TILE_SIZE, DEFAULT_MAP_WIDTH, DEFAULT_MAP_HEIGHT, TOOLS } from '../utils/constants.js';

/**
 * Observable editor state. All map data + UI state lives here.
 * Components subscribe to changes via onChange().
 */
export class EditorState {
  constructor() {
    this._listeners = new Map();
    this._data = {
      // Map metadata
      mapWidth: DEFAULT_MAP_WIDTH,
      mapHeight: DEFAULT_MAP_HEIGHT,
      tileWidth: TILE_SIZE,
      tileHeight: TILE_SIZE,
      orientation: 'orthogonal',
      renderOrder: 'right-down',

      // Layers: array of { name, type, data, visible, locked, opacity, properties, objects }
      layers: [],

      // Tilesets: array of { firstGid, name, image, imageWidth, imageHeight, tileWidth, tileHeight, tileCount, columns, margin, spacing }
      tilesets: [],

      // Tileset images: Map<string, HTMLImageElement>
      tilesetImages: new Map(),

      // Custom map properties
      properties: {},

      // Target engine (from NewMapDialog engine preset)
      targetEngine: null,

      // Tile animations: Map<globalTileId, [{tileId, duration}]>
      tileAnimations: new Map(),

      // Tile collision shapes: Map<globalTileId, [{type: 'rect'|'polygon', ...}]>
      tileCollisions: new Map(),

      // UI state
      activeTool: TOOLS.PENCIL,
      activeLayerIndex: 0,
      selectedTileId: 0,
      selectionStart: null,  // { x, y } tile coords for stamp selection
      selectionEnd: null,
      stamp: null,           // 2D array for multi-tile stamps
      zoom: 1,
      scrollX: 0,
      scrollY: 0,
      cursorTileX: -1,
      cursorTileY: -1,
      showGrid: true,
      showProperties: false,
      dirty: false,
      fileName: null,
    };
  }

  get(key) {
    return this._data[key];
  }

  set(key, value) {
    const old = this._data[key];
    // Always notify for Map/Set values since === compares by reference and
    // callers often mutate the same Map instance.
    if (old === value && !(value instanceof Map) && !(value instanceof Set)) return;
    this._data[key] = value;
    this._notify(key, value, old);
  }

  /** Bulk update without firing per-key notifications until the end. */
  batch(updates) {
    const changed = [];
    for (const [key, value] of Object.entries(updates)) {
      const old = this._data[key];
      if (old !== value || value instanceof Map || value instanceof Set) {
        this._data[key] = value;
        changed.push([key, value, old]);
      }
    }
    for (const [key, value, old] of changed) {
      this._notify(key, value, old);
    }
  }

  /** Subscribe to changes on a specific key or '*' for all changes. */
  onChange(key, fn) {
    if (!this._listeners.has(key)) {
      this._listeners.set(key, new Set());
    }
    this._listeners.get(key).add(fn);
    return () => this._listeners.get(key)?.delete(fn);
  }

  _notify(key, value, old) {
    const keyListeners = this._listeners.get(key);
    if (keyListeners) {
      for (const fn of keyListeners) fn(value, old, key);
    }
    const allListeners = this._listeners.get('*');
    if (allListeners) {
      for (const fn of allListeners) fn(value, old, key);
    }
  }

  // --- Layer helpers ---

  getActiveLayer() {
    return this._data.layers[this._data.activeLayerIndex] || null;
  }

  getTile(layerIndex, x, y) {
    const layer = this._data.layers[layerIndex];
    if (!layer || !layer.data) return 0;
    if (x < 0 || y < 0 || x >= this._data.mapWidth || y >= this._data.mapHeight) return 0;
    return layer.data[y * this._data.mapWidth + x] || 0;
  }

  setTile(layerIndex, x, y, tileId) {
    const layer = this._data.layers[layerIndex];
    if (!layer || !layer.data) return;
    if (x < 0 || y < 0 || x >= this._data.mapWidth || y >= this._data.mapHeight) return;
    layer.data[y * this._data.mapWidth + x] = tileId;
    this.set('dirty', true);
  }

  /** Get a snapshot of tile data for undo purposes. */
  getLayerSnapshot(layerIndex) {
    const layer = this._data.layers[layerIndex];
    if (!layer || !layer.data) return null;
    return { layerIndex, data: new Uint32Array(layer.data) };
  }

  restoreLayerSnapshot(snapshot) {
    const layer = this._data.layers[snapshot.layerIndex];
    if (!layer) return;
    layer.data = new Uint32Array(snapshot.data);
    this._notify('layers', this._data.layers, this._data.layers);
  }

  // --- Tileset helpers ---

  /** Find which tileset a GID belongs to. */
  getTilesetForGid(gid) {
    if (gid <= 0) return null;
    const tilesets = this._data.tilesets;
    for (let i = tilesets.length - 1; i >= 0; i--) {
      if (gid >= tilesets[i].firstGid) return tilesets[i];
    }
    return null;
  }

  /** Get the source rect for a tile GID within its tileset image. */
  getTileRect(gid) {
    const ts = this.getTilesetForGid(gid);
    if (!ts) return null;
    const localId = gid - ts.firstGid;
    const cols = ts.columns || Math.floor(ts.imageWidth / (ts.tileWidth + (ts.spacing || 0)));
    const margin = ts.margin || 0;
    const spacing = ts.spacing || 0;
    const col = localId % cols;
    const row = Math.floor(localId / cols);
    return {
      tileset: ts,
      sx: margin + col * (ts.tileWidth + spacing),
      sy: margin + row * (ts.tileHeight + spacing),
      sw: ts.tileWidth,
      sh: ts.tileHeight,
    };
  }

  // --- New map ---

  createBlankMap(width, height, tileWidth, tileHeight) {
    const size = width * height;
    this.batch({
      mapWidth: width,
      mapHeight: height,
      tileWidth: tileWidth || TILE_SIZE,
      tileHeight: tileHeight || TILE_SIZE,
      layers: [
        { name: 'Ground', type: 'tilelayer', data: new Uint32Array(size), visible: true, locked: false, opacity: 1, properties: {} },
        { name: 'Objects', type: 'tilelayer', data: new Uint32Array(size), visible: true, locked: false, opacity: 1, properties: {} },
        { name: 'Overhead', type: 'tilelayer', data: new Uint32Array(size), visible: true, locked: false, opacity: 1, properties: {} },
      ],
      tilesets: [],
      tilesetImages: new Map(),
      properties: {},
      activeLayerIndex: 0,
      scrollX: 0,
      scrollY: 0,
      zoom: 1,
      dirty: false,
      fileName: null,
    });
  }
}
